import React, { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { formatToWords } from '../utils/formatInWords';

const AssetDistribution = () => {
  const { state } = useAppContext();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const [data, setData] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  useEffect(() => {
    const {
      bonds,
      globalMF,
      globalBonds,
      otherInvestments,
      hybridMF,
      bankBalance,
      fixedDeposit,
      debtMF,
      equityMF,
      unlisted,
      stocks,
      pms,
      globalEquity,
      retirementFund,
      invits,
      gold,
      realEstate
    } = state;

    const total_debt = bankBalance + fixedDeposit + debtMF + retirementFund + bonds + invits + globalBonds + hybridMF;
    const total_equity = equityMF + unlisted + stocks;
    const total_alternative_funds = pms;
    const total_global_equity = globalEquity + globalMF;
    // const total_hybrid = hybridMF;
    const total_gold = gold;
    const total_real_estate = realEstate;
    const total_others = otherInvestments;

    const total = total_debt + total_equity + total_alternative_funds + total_global_equity + total_gold + total_real_estate + total_others;
    setGrandTotal(total);

    const calcPct = (v) => total > 0 ? Math.round((v / total) * 100) : 0;

    const computedData = [
      { name: 'Income', value: calcPct(total_debt), color: '#ef4444', amount: total_debt },
      { name: 'Equity', value: calcPct(total_equity), color: '#3b82f6', amount: total_equity },
      { name: 'Alternative Funds', value: calcPct(total_alternative_funds), color: '#8b5cf6', amount: total_alternative_funds },
      { name: 'Global Equity', value: calcPct(total_global_equity), color: '#06b6d4', amount: total_global_equity },
      // { name: 'Hybrid', value: calcPct(total_hybrid), color: '#84cc16', amount: total_hybrid },
      { name: 'Gold', value: calcPct(total_gold), color: '#f59e0b', amount: total_gold },
      { name: 'Real Estate', value: calcPct(total_real_estate), color: '#10b981', amount: total_real_estate },
      { name: 'Other Investment', value: calcPct(total_others), color: '#10b982', amount: total_others }
    ].filter(item => item.value > 0);

    setData(computedData);
  }, [state]);

  useEffect(() => {
    const loadECharts = async () => {
      if (window.echarts) {
        initChart();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/echarts/5.4.3/echarts.min.js';
      script.onload = initChart;
      document.head.appendChild(script);
    };

    const initChart = () => {
      if (chartRef.current && window.echarts && data.length > 0) {
        if (chartInstance.current) {
          chartInstance.current.dispose();
        }

        chartInstance.current = window.echarts.init(chartRef.current);

        const option = {
          title: {
            text: 'Current Asset Allocation',
            subtext: `Total Portfolio: ₹${formatToWords(grandTotal)}`,
            left: 'center',
            top: 0
          },
          // tooltip: {
          //   trigger: 'item',
          //   formatter: (params) => {
          //     const item = data.find(d => d.name === params.name);
          //     return `${params.seriesName}<br/>${params.name}: ₹${formatInWords(item.amount)} (${params.percent}%)`;
          //   }
          // },
          series: [
            {
              name: 'Portfolio',
              type: 'pie',
              radius: ['40%', '70%'],
              center: ['50%', '60%'],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 6,
                borderColor: '#fff',
                borderWidth: 2
              },
              label: { show: false, position: 'center' },
              emphasis: {
                label: {
                  show: true,
                  fontSize: 14,
                  fontWeight: 'bold',
                  formatter: (params) => {
                    const item = data.find(d => d.name === params.name);
                    return `${params.name}\n₹${formatToWords(item.amount)}\n${params.percent}%`;
                  }
                }
              },
              labelLine: { show: false },
              data: data.map(item => ({
                value: item.value,
                name: item.name,
                itemStyle: { color: item.color }
              }))
            }
          ]
        };

        chartInstance.current.setOption(option);
        window.addEventListener('resize', chartInstance.current.resize);
      }
    };

    loadECharts();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
      window.removeEventListener('resize', () => {});
    };
  }, [data, grandTotal]);

  if (data.length === 0 || grandTotal === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', fontFamily: 'Arial', color: '#666' }}>
        <h3>Asset Allocation</h3>
        <p>No asset data available. Please enter your financial information to see the Allocation.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '6px', maxWidth: '400px', margin: '8px auto', fontFamily: 'Arial' }}>
      <div ref={chartRef} style={{ width: '100%', height: '350px' }} />
      <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '8px' }}>
        <h4 style={{ margin: '0 0 2px', color: '#333', fontSize: '16px', fontWeight: 'bold' }}>Asset Breakdown</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
          {data.map((item) => (
            <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: `2px solid ${item.color}20` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: item.color, borderRadius: '2px' }} />
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{item.name}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>₹{formatToWords(item.amount)}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{item.value}%</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #0ea5e9', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0369a1' }}>Total Portfolio Value: ₹{ formatToWords(grandTotal)}</div>
        </div>
      </div>
    </div>
  );
};

export default AssetDistribution;
